import React, { useEffect, useState } from 'react';
import { Alert, Button } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  WatercraftHelmet,
  WatercraftSpinner,
  WatercraftWordmark,
} from '../../features/watercraft';
import { useVerifyPassCheckoutMutation } from '../../features/watercraft/watercraftPassesSlice';
import {
  PassPurchaseResult,
  resolvePassPurchase,
} from '../../features/watercraft/passPurchaseFlow';
import { WATERCRAFT_SUBMIT_BUTTON_CLASS } from '../../features/watercraft/styles';
import { WATERCRAFT_PASSES_ROUTE } from './constants';

const BACK_BUTTON_CLASS =
  'mt-6 h-11 w-full rounded-lg border-watercraft-border text-base font-semibold text-watercraft-text-primary';

export default function WatercraftPassesSuccessPage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  const [verifyPassCheckout] = useVerifyPassCheckoutMutation();
  const [result, setResult] = useState<PassPurchaseResult | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let cancelled = false;
    resolvePassPurchase({
      sessionId,
      verify: (checkoutSessionId) =>
        verifyPassCheckout({ checkout_session_id: checkoutSessionId }).unwrap(),
      isCancelled: () => cancelled,
    }).then((resolved) => {
      if (!cancelled) {
        setResult(resolved);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [sessionId, verifyPassCheckout]);

  const available = result?.availableCount;

  return (
    <main className='grid min-h-screen w-full place-content-center gap-8 bg-gradient-to-b from-watercraft-bg to-watercraft-surface p-6 text-center text-watercraft-text-primary'>
      <WatercraftHelmet />
      <WatercraftWordmark className='mx-auto h-9 w-auto text-watercraft-text-primary' />
      <section className='mx-auto w-full max-w-sm'>
        {!result && (
          <div className='flex flex-col items-center gap-4'>
            <WatercraftSpinner />
            <p className='text-sm text-watercraft-text-secondary'>
              Confirming your purchase…
            </p>
          </div>
        )}
        {result?.status === 'success' && (
          <>
            <h1 className='mb-3 text-2xl font-bold'>Purchase complete</h1>
            <p className='mb-6 text-sm text-watercraft-text-secondary'>
              {typeof available === 'number'
                ? `You now have ${available} session pass${
                    available === 1 ? '' : 'es'
                  } available.`
                : 'Your session passes are ready.'}
            </p>
            <Button
              type='primary'
              className={WATERCRAFT_SUBMIT_BUTTON_CLASS}
              onClick={() => navigate(WATERCRAFT_PASSES_ROUTE)}
            >
              Done
            </Button>
          </>
        )}
        {result?.status === 'processing' && (
          <>
            <Alert
              type='info'
              showIcon
              message='Payment processing'
              description='Your passes will appear shortly once payment completes.'
            />
            <Button
              className={BACK_BUTTON_CLASS}
              onClick={() => navigate(WATERCRAFT_PASSES_ROUTE)}
            >
              Back to passes
            </Button>
          </>
        )}
        {result?.status === 'failed' && (
          <>
            <Alert
              type='warning'
              showIcon
              message='We could not confirm your purchase'
              description='If you completed payment, your passes may take a moment to appear. Refresh shortly, or try again.'
            />
            <Button
              className={BACK_BUTTON_CLASS}
              onClick={() => navigate(WATERCRAFT_PASSES_ROUTE)}
            >
              Back to passes
            </Button>
          </>
        )}
      </section>
    </main>
  );
}
